<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { standardDateTime } from 'shared/helpers/timeHelper';
import { LLM_PROVIDERS, providerName } from '../llmCredentials';

const props = defineProps({
  credential: { type: Object, required: true },
  isDefault: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete']);

const { t, locale } = useI18n();

// bot_usage_events.cost_micro_cents is in 1e-8 USD.
const MICRO_CENTS_PER_DOLLAR = 100_000_000;

const featureNames = computed(() => ({
  editor: t('CAPTAIN_SETTINGS.FEATURE_NAMES.EDITOR'),
  assistant: t('CAPTAIN_SETTINGS.FEATURE_NAMES.ASSISTANT'),
  copilot: t('CAPTAIN_SETTINGS.FEATURE_NAMES.COPILOT'),
  label_suggestion: t('CAPTAIN_SETTINGS.FEATURE_NAMES.LABEL_SUGGESTION'),
  audio_transcription: t('CAPTAIN_SETTINGS.FEATURE_NAMES.AUDIO_TRANSCRIPTION'),
  conversation_analysis: t(
    'CAPTAIN_SETTINGS.FEATURE_NAMES.CONVERSATION_ANALYSIS'
  ),
  document_faq_generation: t('CAPTAIN_SETTINGS.FEATURE_NAMES.DOCUMENT_FAQ'),
  pdf_faq_generation: t('CAPTAIN_SETTINGS.FEATURE_NAMES.PDF_FAQ'),
  conversation_faq_generation: t(
    'CAPTAIN_SETTINGS.FEATURE_NAMES.CONVERSATION_FAQ'
  ),
  conversation_faq_matching: t('CAPTAIN_SETTINGS.FEATURE_NAMES.FAQ_MATCHING'),
  help_center_article_generation: t(
    'CAPTAIN_SETTINGS.FEATURE_NAMES.HELP_CENTER_ARTICLES'
  ),
  help_center_query_translation: t(
    'CAPTAIN_SETTINGS.FEATURE_NAMES.HELP_CENTER_TRANSLATION'
  ),
  help_center_search: t('CAPTAIN_SETTINGS.FEATURE_NAMES.HELP_CENTER_SEARCH'),
  onboarding_content_generation: t('CAPTAIN_SETTINGS.FEATURE_NAMES.ONBOARDING'),
}));

const periodNames = computed(() => ({
  day: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.TODAY'),
  week: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.THIS_WEEK'),
  month: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.THIS_MONTH'),
}));

const numberLocale = computed(() => locale.value.replace('_', '-'));

const formatNumber = value =>
  new Intl.NumberFormat(numberLocale.value).format(value || 0);

const cost = computed(() =>
  new Intl.NumberFormat(numberLocale.value, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(
    (props.credential.cost_micro_cents_used || 0) / MICRO_CENTS_PER_DOLLAR
  )
);

const usageShare = computed(() =>
  Math.min(
    100,
    (props.credential.tokens_used / props.credential.token_limit) * 100
  )
);

const usageLine = computed(() => {
  const {
    tokens_used: used,
    token_limit: limit,
    limit_period: period,
  } = props.credential;
  return limit
    ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.WITH_LIMIT', {
        used: formatNumber(used),
        limit: formatNumber(limit),
        period: periodNames.value[period],
      })
    : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.NO_LIMIT', {
        used: formatNumber(used),
        period: periodNames.value[period],
      });
});

const hasIssue = computed(
  () => props.credential.limit_reached || props.credential.last_error_at
);

// The provider's own words follow this line, so it only says when, and from which feature when
// the call came from one (embeddings come from none).
const failureTitle = computed(() => {
  const { last_error_at: at, last_error_feature: feature } = props.credential;
  const time = standardDateTime(at);
  return feature
    ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FAILURE.TITLE_WITH_FEATURE', {
        feature: featureNames.value[feature] || feature,
        time,
      })
    : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FAILURE.TITLE', { time });
});

// Built here rather than in the template: the separators are raw text.
const subtitle = computed(() =>
  [
    providerName(props.credential.provider),
    props.credential.endpoint,
    props.credential.model_override,
  ]
    .filter(Boolean)
    .join(' · ')
);

// The features people pick a model for come by name; the background ones (FAQ drafting, search
// translation...) would bury them, so they are counted and listed in a tooltip.
const MAIN_FEATURES = [
  'editor',
  'assistant',
  'copilot',
  'audio_transcription',
  'label_suggestion',
  'conversation_analysis',
];

const mainFeatures = computed(() =>
  props.credential.features.filter(feature => MAIN_FEATURES.includes(feature))
);

const otherFeatures = computed(() =>
  props.credential.features
    .filter(feature => !MAIN_FEATURES.includes(feature))
    .map(feature => featureNames.value[feature] || feature)
);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-xl outline outline-1 -outline-offset-1 bg-n-solid-1"
    :class="hasIssue ? 'outline-n-ruby-6' : 'outline-n-weak'"
  >
    <div class="flex items-start gap-3">
      <span
        class="grid flex-shrink-0 rounded-lg size-9 place-content-center bg-n-alpha-2 text-n-slate-12"
      >
        <Icon
          :icon="LLM_PROVIDERS[credential.provider]?.icon || 'i-lucide-cpu'"
          class="size-5"
        />
      </span>
      <div class="flex flex-col flex-1 min-w-0 gap-0.5">
        <div class="flex flex-wrap items-center gap-2">
          <h4 class="m-0 text-sm font-medium truncate text-n-slate-12">
            {{ credential.name }}
          </h4>
          <span
            v-if="isDefault"
            v-tooltip="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DEFAULT_HINT')"
            class="px-1.5 py-0.5 text-xs rounded bg-n-blue-3 text-n-blue-11"
          >
            {{
              t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DEFAULT', {
                provider: providerName(credential.provider),
              })
            }}
          </span>
          <span
            v-if="credential.limit_reached"
            class="px-1.5 py-0.5 text-xs font-medium rounded bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.LIMIT_REACHED') }}
          </span>
          <span
            v-if="credential.last_error_at"
            class="px-1.5 py-0.5 text-xs font-medium rounded bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FAILURE.BADGE') }}
          </span>
        </div>
        <p class="m-0 text-xs truncate text-n-slate-11">{{ subtitle }}</p>
      </div>
      <div class="flex items-center flex-shrink-0 gap-1">
        <NextButton
          v-tooltip="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.EDIT')"
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-pencil"
          @click="emit('edit', credential)"
        />
        <NextButton
          v-tooltip="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DELETE')"
          type="button"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          @click="emit('delete', credential)"
        />
      </div>
    </div>

    <div
      v-if="credential.last_error_at"
      class="flex items-start gap-2 p-3 rounded-lg bg-n-ruby-2 outline outline-1 -outline-offset-1 outline-n-ruby-5"
    >
      <Icon
        icon="i-lucide-plug-zap"
        class="flex-shrink-0 size-4 mt-0.5 text-n-ruby-11"
      />
      <div class="flex flex-col min-w-0 gap-1">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ failureTitle }}
        </p>
        <p class="m-0 text-xs break-words text-n-slate-12">
          {{ credential.last_error_message }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FAILURE.HINT') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <div
        v-if="credential.token_limit"
        class="w-full h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
      >
        <div
          class="h-full transition-all rounded-full"
          :class="credential.limit_reached ? 'bg-n-ruby-9' : 'bg-n-teal-9'"
          :style="{ width: `${usageShare}%` }"
        />
      </div>
      <p class="flex flex-wrap m-0 text-xs gap-x-3 text-n-slate-11">
        <span>{{ usageLine }}</span>
        <span>
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USAGE.COST', { cost }) }}
        </span>
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-1.5">
      <span class="text-xs text-n-slate-11">
        {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.USED_BY') }}
      </span>
      <span
        v-for="feature in mainFeatures"
        :key="feature"
        class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
      >
        {{ featureNames[feature] || feature }}
      </span>
      <span
        v-if="otherFeatures.length"
        v-tooltip="otherFeatures.join(', ')"
        class="px-2 py-0.5 text-xs rounded-md cursor-default bg-n-alpha-1 text-n-slate-11"
      >
        {{
          t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.OTHER_FEATURES', {
            count: otherFeatures.length,
          })
        }}
      </span>
      <span
        v-if="!credential.features.length"
        class="text-xs italic text-n-slate-10"
      >
        {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.UNUSED') }}
      </span>
    </div>
  </div>
</template>
